import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import classNames from 'classnames';
import get from 'lodash.get';

import TeasePicture from 'components/TeasePicture';
import Ad from 'components/Ad';
import Link from 'components/Link';
import Unibrow from 'components/Unibrow';
import PaidContent from 'components/PaidContent';
import TypeIcon from 'components/TypeIcon';

import AIMS_FLAVORS from 'lib/aimsFlavors';
import { package as PackagePropType } from 'lib/CustomPropTypes';
import { timeFrom } from 'lib/DateTime';
import { imageAltForItem } from 'lib/imageAlt';
import { teaseList as paidContent } from 'lib/PaidContent';

import { TeaseListContainer, TeaseListItemContainer } from './containers';
import SeeAllButton from './SeeAllButton';
import styles from './styles.module.scss';

class TeaseList extends Component {
  static propTypes = {
    content: PackagePropType.isRequired,
    vertical: PropTypes.string.isRequired,
    route: PropTypes.string,
    pkgClassName: PropTypes.string,
  };

  static defaultProps = {
    route: '/',
    pkgClassName: 'pkg teaseList',
  };

  constructor(props) {
    super(props);

    const { content: { items } } = props;

    this.state = {
      items,
      boxAdLoaded: false,
      paidContentLoaded: false,
      topHeight: 0,
    };
  }

  componentDidMount() {
    let { content: { items } } = this.props;
    // On first render, insert paid content in-between 3rd and 4th items
    if (items.length >= 3) {
      items = [...items.slice(0, 3), paidContent, ...items.slice(3)];
    }
    this.setState({ items });
  }

  renderArticleTeaseListItemContainer = (article) => {
    const { paidContentLoaded } = this.state;
    const { content: { metadata: packageMetadata }, vertical } = this.props;
    const {
      id, item, computedValues, metadata: contentMetadata, type,
    } = article;

    const isToday = vertical === 'today';

    if (type === paidContent.type) {
      const isNewsOrMsnbc = vertical === 'news' || vertical === 'msnbc';
      const toolkitStyling = (isNewsOrMsnbc && ['pl5', 'pt5', 'pb5', 'pl7-m', 'pt7-m', 'pb7-m', 'pl5-l', 'pt5-l', 'pb5-l']) || '';
      const showContent = paidContentLoaded ? 'db' : 'dn';
      return (
        <PaidContent
          {...article}
          className={classNames(toolkitStyling, showContent, 'relative', {
            [styles.article]: !isToday,
            [styles.trendingArticle]: isToday,
          })}
          key={paidContent.type}
          adLoaded={this.setPaidContentLoaded}
        />
      );
    }

    if (!computedValues) return null;

    const {
      headline,
      url,
      teaseImage,
      unibrow,
      alternateTeaseImages = [],
    } = computedValues;


    const isAd = get(item, 'nativeAd');

    let eyebrowClass = styles.eyebrow;
    if (isAd) {
      eyebrowClass = styles[isToday ? 'trendingPaid' : 'paid'];
    }

    const metadata = {
      ...packageMetadata,
      ...contentMetadata,
    };

    // Default for showTeaseImage on TeaseList is false
    const showTeaseImage = metadata.showTeaseImage && teaseImage;

    const showTimestamp = !isToday && !isAd && !!item;
    const showDot = !isAd && !isToday;

    // TODO: properly align all variations of content next to teaseImage when shown
    // Hack below adds &nbsp; so that headline is properly aligned when no unibrow.text set
    const showEyebrow = isToday && (isAd || unibrow.text || showTeaseImage);

    const eyebrowToolkitClasses = 'f2 lh-none founders-mono fw1 mb2';

    const itemClasses = [{
      [styles.sponsored]: isAd,
      [styles.showDot]: showDot,
      [styles.alignDotToHed]: showDot && !showTimestamp, // dot is aligned center if no timestamp
    }];

    if (isToday) {
      itemClasses.push(styles.trendingArticle);
      itemClasses.push('pv5 ph0-l');
    } else {
      itemClasses.push(styles.article);
      itemClasses.push('mt5 mt7-m mt5-l'); // top ( must be margin for dot positioning)
      itemClasses.push('pb5 pb7-m pb5-l'); // bottom
      itemClasses.push('pl5 pl7-m pl5-l'); // left
    }

    const teaseImageWith1X1AspectRatio = alternateTeaseImages.find(({ aspectRatio }) => aspectRatio === 'ASPECT_1X1');
    const teaseImageWith1X1AspectRatioURL = get(teaseImageWith1X1AspectRatio, 'image.url.primary', '');
    const teaseImageURL = get(teaseImage, 'url.primary', '');

    return (
      <TeaseListItemContainer key={id} itemId={id} itemClasses={classNames(itemClasses)}>
        {isToday && showTeaseImage && (
          <Link to={url}>
            <div
              className={classNames(styles.trendingPicture, 'relative h-100')}
            >
              <TeasePicture
                className={styles.image}
                url={teaseImageWith1X1AspectRatioURL || teaseImageURL}
                responsiveFlavors={{
                  s: AIMS_FLAVORS.FOCAL_80x80,
                }}
                alt={imageAltForItem(article)}
                preview={
                  type === 'video' && { url, redirect: false, radius: true }
                }
                retina
              />
              <TypeIcon type={type} className={styles.typeIcon} />
            </div>
          </Link>
        )}

        <div className={styles.info}>
          {isAd && <Ad slot="native" />}

          {showTimestamp
            && (
            <div className={classNames(styles.timestamp, eyebrowToolkitClasses)}>
              {i18next.t('time_ago', { time: timeFrom(item.dateCreated) })}
            </div>
            )}

          {showEyebrow
            && (
            <Unibrow
              className={classNames(eyebrowClass, eyebrowToolkitClasses, 'ttu')}
              unibrow={({
                ...unibrow,
                text: unibrow.text || <span>&nbsp;</span>,
                override: isAd ? i18next.t('Paid Content') : null,
              })}
            />
            )}

          <h2 className={styles.title}>
            <Link to={url}>{headline}</Link>
          </h2>

          {isAd && item.source.organization
            && <p className={styles.adSource}>{item.source.organization.name.toUpperCase()}</p>}

          {!isToday
            && showTeaseImage
            && (
              <Link to={url} className={styles.teasePicture}>
                <TeasePicture
                  url={teaseImage.url.primary}
                  responsiveFlavors={{
                    s: AIMS_FLAVORS.FOCAL_760x380,
                    m: AIMS_FLAVORS.FOCAL_1000x500,
                    l: AIMS_FLAVORS.FOCAL_260x130,
                  }}
                  alt={imageAltForItem(article)}
                  preview={type === 'video' && { url, redirect: false }}
                />
              </Link>
            )}
        </div>
      </TeaseListItemContainer>
    );
  };

  // Return the article list markup
  renderTeaseListItems = () => {
    const { route, vertical } = this.props;
    const { items } = this.state;

    const isToday = vertical === 'today';
    const isCover = route === '/';

    const classes = classNames(
      styles.articles,
      'ph5 ph0-m',
      {
        [styles.itemLimit7]: (!isCover || (isToday && isCover)),
      },
    );

    return (
      <div className={styles.articlesWrapper}>
        <ul className={classes}>
          {items.filter((item) => item).map(this.renderArticleTeaseListItemContainer)}
        </ul>
      </div>
    );
  };

  renderBoxAd = () => {
    const { boxAdLoaded } = this.state;
    const adAboveList = get(this.props, 'content.adAboveList', false);

    const slot = get(this.props, 'content.adType');

    if (!slot) return null;

    const offsetViewport = slot === 'boxrail' || slot === 'boxflex'
      ? 100
      : 50;

    const adClasses = classNames(
      styles.ad,
      {
        mb8: boxAdLoaded && adAboveList,
        mt8: boxAdLoaded && !adAboveList,
      },
    );

    return (
      <div className={adClasses} data-test="teaseListAd">
        <Ad
          slot={slot}
          packageId={get(this.props, 'content.id')}
          offsetViewport={offsetViewport}
          handler={(enigmaAd, { EVENTS }) => {
            enigmaAd.on(EVENTS.RENDER, (event) => {
              if (!event || event.empty) {
                return;
              }
              this.setState((prevState) => (
                // TODO: Magic number 300 should be pulled from boxAd height definition
                { topHeight: prevState.topHeight + 300, boxAdLoaded: true }
              ));
            });
          }}
        />
      </div>
    );
  };

  renderAd = () => {
    const {
      content: { id, adType },
    } = this.props;

    return (
      adType
      && this.renderBoxAd({
        slot: adType,
        packageId: id,
        offsetViewport: 50,
      })
    );
  };

  setPaidContentLoaded = (loaded) => {
    this.setState({ paidContentLoaded: loaded });
  };

  renderSeeAllButton = () => {
    const { content } = this.props;
    const {
      metadata: { seeAllUrl, seeAllText },
    } = content;

    return (
      <SeeAllButton seeAllUrl={seeAllUrl} seeAllText={seeAllText} />
    );
  }

  render() {
    return (
      <TeaseListContainer
        {...this.props}
        ad={this.renderAd()}
      >
        <div className={styles.articlesAndButton}>
          {this.renderTeaseListItems()}
          {this.renderSeeAllButton()}
        </div>
      </TeaseListContainer>
    );
  }
}

export default TeaseList;
